<script setup lang="ts">
import ReferencesDetailHeaderImage from './ReferencesDetailHeaderImage.vue'
import BackButton from '~/components/global/BackButton.vue'
import Container from '~/components/layout/Container.vue'
import type { ReferenceDetailResponse } from '~/types/api/ReferenceDetail'

const { t } = useI18n()
const router = useRouter()
const props = defineProps<{
  referenceDetail: ReferenceDetailResponse
}>()

const lastRoutePath = computed(() => {
  let back = router.options.history.state.back

  if (back) {
    back = back.toString().split('?')[0].split('#')[0]
  }

  return back
})

const listingLocaleLink = useLocaleLink({
  name: 'references',
})

const backText = computed(() => {
  if (lastRoutePath.value === listingLocaleLink?.path) {
    return {
      long: t(`pages.references.detail.header.back.long`),
      short: t(`pages.references.detail.header.back.short`),
    }
  }

  return null
})

const highlights = computed(() => {
  const highlights = []

  if (props.referenceDetail.module_data.premises_size) {
    highlights.push({
      label: t('pages.references.detail.header.premisesSizeLabel'),
      value: props.referenceDetail.module_data.premises_size,
    })
  }

  if (props.referenceDetail.module_data.domain) {
    highlights.push({
      label: props.referenceDetail.module_data.domain_title,
      value: props.referenceDetail.module_data.domain,
    })
  }

  return highlights
})
</script>

<template>
  <div class="c-references-detail-header">
    <Container class="c-references-detail-header__container">
      <div class="c-references-detail-header__back">
        <BackButton
          :back-text="backText?.long"
          :back-text-short="backText?.short"
        />
      </div>

      <div class="c-references-detail-header__main">
        <h1 class="c-references-detail-header__title">
          {{ referenceDetail.name }}
        </h1>

        <div
          v-if="referenceDetail.module_data.location"
          class="c-references-detail-header__location"
        >
          {{ referenceDetail.module_data.location }}
        </div>

        <div
          v-if="highlights.length"
          class="c-references-detail-header__highlights"
        >
          <div
            v-for="highlight in highlights"
            :key="highlight.label"
            class="c-references-detail-header__highlight"
          >
            <span class="sc-label">{{ highlight.label }}</span>
            <div class="sc-value" v-html="highlight.value" />
          </div>
        </div>

        <div class="c-references-detail-header__bar" />
      </div>

      <ReferencesDetailHeaderImage
        :reference-detail="referenceDetail"
        class="c-references-detail-header__image"
      />
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-references-detail-header {
  padding-top: 48px;

  @include breakpoint-down('lg') {
    padding-top: 32px;
  }

  &__container {
    display: grid;
    grid-template:
      'back back' auto
      'main image' auto
      / 1fr auto;
    gap: 70px 100px;
    align-items: center;

    @include breakpoint-down('xl') {
      gap: 70px 60px;
    }

    @include breakpoint-down('lg') {
      gap: 32px 50px;
    }

    @include breakpoint-down('md') {
      grid-template:
        'back' auto
        'main' auto
        'image' auto
        / 1fr;
    }
  }

  &__back {
    grid-area: back;
    justify-self: start;
  }

  &__main {
    position: relative;
    z-index: 2;
  }

  &__image {
    align-self: stretch;
    width: 620px;

    @include breakpoint-down('xl') {
      width: 500px;
    }

    @include breakpoint-down('lg') {
      width: 460px;
    }

    @include breakpoint-down('md') {
      width: 100%;
    }

    img {
      width: 100%;
      aspect-ratio: 620 / 480;
      height: auto;
      object-fit: cover;
    }
  }

  &__title {
    font-size: 58px;
    line-height: 0.96;
    margin: 0;

    @include breakpoint-down('xl') {
      font-size: 46px;
    }

    @include breakpoint-down('lg') {
      font-size: 42px;
    }
  }

  &__location {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.32;
    color: var(--color-primary);
    margin-top: 24px;

    @include breakpoint-down('xl') {
      font-size: 22px;
      margin-top: 16px;
    }

    @include breakpoint-down('lg') {
      font-size: 20px;
      margin-top: 14px;
    }
  }

  &__highlights {
    margin-top: 60px;
    display: flex;
    flex-wrap: wrap;
    gap: 32px 64px;

    @include breakpoint-down('xl') {
      margin-top: 50px;
      gap: 24px 48px;
    }

    @include breakpoint-down('lg') {
      margin-top: 38px;
    }

    @include breakpoint-down('xs') {
      margin-top: 32px;
      gap: 16px 32px;
    }
  }

  &__highlight {
    display: flex;
    flex-direction: column;

    .sc-label {
      font-size: 18px;
      line-height: 1.1;

      @include breakpoint-down('xl') {
        font-size: 16px;
      }

      @include breakpoint-down('xs') {
        font-size: 15px;
      }
    }

    .sc-value {
      font-size: 28px;
      line-height: 1.1;
      font-weight: 600;
      color: var(--color-primary);
      margin-top: 2px;

      @include breakpoint-down('xl') {
        font-size: 22px;
      }

      @include breakpoint-down('lg') {
        font-size: 20px;
      }

      @include breakpoint-down('xs') {
        font-size: 18px;
      }
    }
  }

  &__bar {
    margin-top: 60px;
    height: 4px;
    background-color: var(--color-secondary);
    margin-right: -340px;

    @include breakpoint-down('xl') {
      margin-top: 40px;
      margin-right: -300px;
    }

    @include breakpoint-down('lg') {
      margin-top: 32px;
    }

    @include breakpoint-down('md') {
      display: none;
    }
  }
}
</style>
